import React, { useState } from 'react';
import Head from 'next/head';
import {
  AditionInitialization,
  AditionTagPlacement,
  AditionTagWithFallback,
  Image,
} from '@vgn-medien-holding/vgn-fe-components';
import { getCookie } from 'cookies-next';
import type { GetStaticPaths, GetStaticProps } from 'next';
import { twMerge } from 'tailwind-merge';
import { EntityInfo } from '@components/atoms/EntityInfo/EntityInfo';
import { Meta } from '@components/atoms/MetaTags/Meta';
import { Oewa } from '@components/atoms/Oewa/Oewa';
import { Title } from '@components/atoms/Title';
import { EntityMix } from '@components/organisms/EntityMix/EntityMix';
import { personenSsrLogic, Persons } from '@components/pages/personen';
import { Default } from '@components/templates/layouts/Default';
import { isUuid } from '@utils/common';
import { calculateAge, formatDate } from '@utils/dateHelpers';
import { GetArticleByIdDocument, GetBySlugDocument } from '@src/lib/graphql/generated';
import { decodeOptions } from '@src/utils/urlHelpers';
import {
  GetPersonByIdDocument,
  GetPersonBySlugDocument,
  GetPersonMoviesDocument,
  GetPersonShowsDocument,
} from '@lib/graphql/generated';
import { createUrqlClient } from '@lib/graphql/urql';
import { useTagManager } from '@lib/hooks/useTagManager';
import { handleRedirect } from '@lib/redirects/redirect';
import { ArticleBody } from '../[categoryOrPage]/[slug]';

function Person({ data, slug }) {
  return slug === 'index' ? <Persons /> : <PersonSlug data={data} />;
}

// Unterseite Stars
const ad_settings_UnterseiteStars = {
  billboard: {
    id: 5256692,
    name: 'TVM_Unterseite_Stars_Billboard',
    yieldLab: [],
    breakpoints: [3, 4, 5, 6],
  },
  mobile_top: {
    id: 5256641,
    name: 'TVM_Mobile_Unterseite_Stars_HPA',
    yieldLab: [],
    breakpoints: [0, 1, 2, 3],
  },
  mobile_middle1: {
    id: 5256615,
    name: 'TVM_Mobile_Unterseite_Stars_MedRec_Cad',
    yieldLab: [],
    breakpoints: [0, 1, 2, 3],
  },
};

function PersonSlug({ data }) {
  const { person, article, oewa } = data;
  const [filters, setFilters] = useState({
    entityType: null,
    provider: null,
  });

  useTagManager({
    event: 'metadata',
    loginStatus: 'nicht eingeloggt',
    seitentyp: 'Person',
    titel: person.name,
    assetID: oewa?.id,
    aktualisierungsdatum: data?.updated_at,
    erstellungsdatum: data?.created_at,
    oewaSeitenkontingent: oewa?.page_key || 'RedCont',
    oewaSubkontingent: oewa?.category_key || 'KulturUndFreizeit',
    oewaProfilingkontingent: oewa?.profile_key || 'KulturUeberblick',
    cookieAlert: getCookie('OptanonConsent'),
  });

  const appearanceCount = (person?.movies?.paginatorInfo?.total || 0) + (person?.shows?.paginatorInfo?.total || 0);

  return (
    <Default>
      <AditionInitialization data={{ ad_settings: ad_settings_UnterseiteStars }} />

      <aside>
        <AditionTagPlacement
          tag={ad_settings_UnterseiteStars.mobile_top}
          classProps={{ root: 'mb-4 grid place-items-center lg:hidden' }}
        />
      </aside>
      <Oewa
        pageKey={oewa?.page_key || 'RedCont'}
        categoryKey={oewa?.category_key || 'KulturUndFreizeit'}
        profileKey={oewa?.profile_key || 'KulturUeberblick'}
      />
      <Head>{Meta(person.metadata, person.metadata?.meta_title + ' | TV-MEDIA')}</Head>
      <main className="dark grid text-white prose-a:text-white">
        <div className="row-start-2 mt-8 grid flex-wrap gap-4 text-center lg:row-start-auto lg:mt-0 lg:flex lg:items-center lg:justify-center">
          <select
            className="input-tvm-classic lg:giant !min-w-80"
            value={filters.entityType || ''}
            onChange={(item) => {
              setFilters((prev) => ({ ...prev, entityType: item?.target.value }));
            }}
          >
            <option value="all">Alle Inhalte</option>
            <option value="movies">Filme</option>
            <option value="shows">Serien</option>
          </select>
        </div>
        <article className="row-start-1 mt-12 px-4 sm:px-8 md:px-16 lg:row-start-auto">
          <section
            className={twMerge(
              'mx-auto grid gap-10 border-y border-gray-650/25 bg-gray-820/70 px-4 py-8 text-center sm:px-12 lg:grid-cols-[auto,1fr] lg:justify-start lg:text-left',
              person.died_at ? 'max-w-[1240px]' : 'max-w-[1090px]',
            )}
          >
            <div className="relative mx-auto size-52 overflow-hidden rounded-full">
              {person?.image?.url && (
                <Image
                  priority
                  src={person.image.url}
                  alt={person.name}
                  fill
                  sizes="400px"
                  classProps={{ root: 'object-cover' }}
                  copyright={person?.image?.copyright_text}
                />
              )}
            </div>
            <div>
              <Title level={1} classProps={{ heading: 'text-3xl lg:text-4xl' }}>
                {person.name}
              </Title>
              <div
                dangerouslySetInnerHTML={{ __html: person.summary }}
                className="mt-3 line-clamp-5 lg:line-clamp-2"
              ></div>
              <div className="overflow-hidden">
                <div className="lg:divider-container mt-8 hidden gap-y-3 [&>div:first-child]:pr-10 [&>div:not(:first-child)]:px-10">
                  {appearanceCount > 0 && (
                    <EntityInfo
                      classProps={{
                        value: 'text-base',
                        label: 'text-gray-400',
                        root: 'divider-item w-40 pl-10',
                      }}
                      value={appearanceCount}
                      label="Auftritte"
                    />
                  )}
                  {person?.gender != 0 && person?.gender != 4 && (
                    <EntityInfo
                      classProps={{ value: 'text-base', label: 'text-gray-400', root: 'divider-item w-40' }}
                      value={person.gender == 1 ? 'Weiblich' : person.gender == 2 ? 'Männlich' : 'Divers'}
                      label="Geschlecht"
                    />
                  )}
                  <EntityInfo
                    classProps={{ value: 'text-base', label: 'text-gray-400', root: 'divider-item w-40 pl-0' }}
                    value={person.born_at ? formatDate(person.born_at) : 'k.A.'}
                    label="Geboren am"
                  />
                  {person.died_at && (
                    <EntityInfo
                      classProps={{ value: 'text-base', label: 'text-gray-400', root: 'divider-item w-40 pl-0' }}
                      value={formatDate(person.died_at)}
                      label="Verstorben am"
                    />
                  )}
                  <EntityInfo
                    classProps={{ value: 'text-base', label: 'text-gray-400', root: 'divider-item w-40' }}
                    value={person.born_at ? calculateAge(person.born_at, person.died_at) : 'k.A.'}
                    label="Alter"
                  />
                </div>
              </div>
            </div>
          </section>

          <aside>
            <AditionTagWithFallback
              tag={ad_settings_UnterseiteStars.billboard}
              fallback={ad_settings_UnterseiteStars.mobile_middle1}
              breakpoint={'lg'}
              classProps={{ root: 'my-5 grid place-items-center' }}
            />
          </aside>
        </article>
        <EntityMix
          classProps={{ root: 'mt-16' }}
          movieVariables={{ id: person.id, first: 10, page: 1 }}
          alternateMovieQuery={GetPersonMoviesDocument}
          alternateMoviePath={['person', 'movies']}
          movieSkip={filters?.entityType === 'shows'}
          tvshowVariables={{ id: person.id, page: 1, first: 10 }}
          alternateTvshowQuery={GetPersonShowsDocument}
          alternateTvshowPath={['person', 'shows']}
          tvshowSkip={filters?.entityType === 'movies'}
        />
        {article && (
          <article className="mx-auto mt-32 max-w-[1090px] px-4 md:px-8">
            <Title classProps={{ heading: 'px-8 lg:px-32 text-3xl leading-11 lg:text-4xl lg:leading-12' }} level={1}>
              {article.headline}
            </Title>
            <div>
              <ArticleBody content={article.content} />
            </div>
          </article>
        )}
      </main>
    </Default>
  );
}

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: ['/personen/index'],
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const { ssrQuery, ssrCache } = createUrqlClient();

  const options = decodeOptions(params?.slug);

  if (options?.slug === 'index') {
    return await personenSsrLogic();
  }

  const { data: personData } = isUuid(options.slug)
    ? await ssrQuery({
        query: GetPersonByIdDocument,
        variables: { id: options.slug },
      })
    : await ssrQuery({
        query: GetPersonBySlugDocument,
        variables: { slug: options.slug },
      });
  const person = personData?.person;

  // Handle redirect result
  if (person?.metadata?.redirect) {
    return handleRedirect(301, person?.metadata?.redirect);
  }

  if (!person) {
    // Check if a redirect is in place
    const { data: pageData } = await ssrQuery({
      query: GetBySlugDocument,
      variables: {
        portal: 'tvmedia',
        slug: `/personen/${options.slug}`,
      },
    });

    // Handle redirect result
    if (pageData?.findBySlug?.__typename === 'Redirect' && pageData?.findBySlug?.redirect_type) {
      return handleRedirect(
        pageData?.findBySlug?.redirect_type,
        pageData?.findBySlug?.redirect_target != null ? pageData?.findBySlug?.redirect_target : '',
      );
    }
  }

  if (!person) return { notFound: true };

  const data = { person, article: null };

  if (person.article_id) {
    const { data: articleData } = await ssrQuery({
      query: GetArticleByIdDocument,
      variables: { id: person.article_id },
    });
    data.article = articleData?.article;
  }

  await ssrQuery({
    query: GetPersonMoviesDocument,
    variables: { id: person.id, first: 10, page: 1 },
  });

  await ssrQuery({
    query: GetPersonShowsDocument,
    variables: { id: person.id, page: 1, first: 10 },
  });

  return {
    revalidate: 60 * 60 * 24 * 2, // 2 days
    props: {
      data,
      urqlState: ssrCache.extractData(),
    },
  };
};

export default Person;
